import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import {
  getBusinessNotifications,
  getNotificationUnreadCount,
  markAllNotificationsRead,
  markNotificationRead
} from '../services/api/implementation/impl';
import {
  notificationLink,
  priorityDotClass,
  relativeTime
} from '../../shared/utils/notificationLinks';
import { groupNotifications, stackSummary } from '../../shared/utils/notificationGroups';

/**
 * The full notification history.
 *
 * Tabs are driven by the unread-count response rather than a hard-coded list of
 * verticals: a salesperson has no reason to see an empty Inventory tab, and the
 * backend already returns the per-vertical breakdown the badge needs.
 *
 * Rows arrive one per event and are drawn stacked by day and by kind, because a
 * day that produced eleven follow-up reminders should read as one line saying
 * eleven, not as eleven lines saying one. Stacking can be switched off for the
 * times the flat log is what you want — an audit, or a search by eye — and the
 * choice is remembered.
 */

const PAGE_SIZE = 20;

const STACK_STORAGE_KEY = 'notifications.stackSimilar';

/** Stacking is on by default; the reader's last choice wins over that. */
function readStackPreference() {
  try {
    return window.localStorage.getItem(STACK_STORAGE_KEY) !== 'off';
  } catch {
    // Private mode and blocked storage both throw here. Losing the preference
    // is not worth losing the screen over.
    return true;
  }
}

function writeStackPreference(stack) {
  try {
    window.localStorage.setItem(STACK_STORAGE_KEY, stack ? 'on' : 'off');
  } catch {
    // Nothing to do — the session keeps the choice in component state anyway.
  }
}

const VERTICAL_LABELS = {
  Sales: 'Sales',
  Accounts: 'Accounts',
  Operations: 'Operations',
  Inventory: 'Inventory',
  Management: 'Management',
  Marketing: 'Marketing',
  HR: 'HR'
};

/**
 * One notification row.
 *
 * Kept separate so an expanded stack renders the same row the stack collapsed,
 * rather than a near-copy that drifts the first time one of them is touched.
 * The vertical badge is dropped inside a stack: every member shares it with the
 * stack header by construction.
 */
const NotificationRow = ({ notification, onOpen, onMarkRead, compact = false }) => {
  const isUnread = !notification.read_at;
  const link = notificationLink(notification);
  return (
    <div
      role={link ? 'button' : undefined}
      tabIndex={link ? 0 : undefined}
      onClick={link ? () => onOpen(notification) : undefined}
      onKeyDown={
        link
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(notification);
              }
            }
          : undefined
      }
      className={`flex gap-3 border-b border-gray-100 last:border-b-0 ${
        compact ? 'px-5 py-2.5' : 'px-5 py-4'
      } ${link ? 'cursor-pointer hover:bg-gray-50' : ''} ${isUnread ? 'bg-blue-50/40' : ''}`}>
      <span
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
          isUnread ? 'bg-blue-500' : priorityDotClass(notification.priority)
        }`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span
            className={`text-sm ${
              isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'
            }`}>
            {notification.title}
          </span>
          <span className="shrink-0 text-xs text-gray-400">
            {relativeTime(notification.created_at)}
          </span>
        </div>
        <p className="mt-1 text-sm leading-snug text-gray-600">{notification.body}</p>
        {!compact && (
          <span className="mt-1.5 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
            {VERTICAL_LABELS[notification.vertical] || notification.vertical}
          </span>
        )}
      </div>

      {isUnread && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead(notification.id);
          }}
          className="shrink-0 self-start text-xs font-medium text-blue-600 hover:text-blue-700">
          Mark read
        </button>
      )}
    </div>
  );
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collapsed } = useSidebar();
  const [vertical, setVertical] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [stack, setStack] = useState(readStackPreference);
  const [expanded, setExpanded] = useState(() => new Set());

  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getNotificationUnreadCount,
    refetchInterval: 60000,
    retry: 1
  });

  const listKey = ['notifications', 'list', { vertical, unreadOnly, page }];
  const { data: listData, isLoading, isError } = useQuery({
    queryKey: listKey,
    queryFn: () =>
      getBusinessNotifications({
        page,
        limit: PAGE_SIZE,
        vertical: vertical || undefined,
        unread: unreadOnly
      }),
    // Opening this screen is a deliberate act of going to look; serving it from
    // a cache the app keeps fresh for 100 minutes (App.js) would answer that
    // with a list from before lunch.
    staleTime: 0,
    retry: 1
  });

  const byVertical = countData?.data?.byVertical || {};
  const unreadTotal = countData?.data?.total ?? 0;
  const notifications = Array.isArray(listData?.data) ? listData.data : [];
  const total = listData?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markRead = useMutation({ mutationFn: markNotificationRead, onSuccess: invalidate });
  // One mutation for a whole stack, rather than one invalidation per row: a
  // stack of twenty would otherwise refetch the list twenty times.
  const markGroupRead = useMutation({
    mutationFn: (ids) => Promise.all(ids.map((id) => markNotificationRead(id))),
    // Settled, not success: if one of the calls fails the others still went
    // through, and leaving those rows drawn as unread until the next refetch
    // would be a lie about what the server holds.
    onSettled: invalidate
  });
  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(vertical || undefined),
    onSuccess: invalidate
  });

  // Only offer tabs for verticals the user actually receives. A vertical the
  // user has read everything in still belongs here, so the tab list is seeded
  // from whatever the current page contains as well as the unread breakdown.
  const verticalsInPlay = Array.from(
    new Set([...Object.keys(byVertical), ...notifications.map((n) => n.vertical)])
  ).filter(Boolean);

  const days = useMemo(() => groupNotifications(notifications, { stack }), [notifications, stack]);

  const select = (nextVertical) => {
    setVertical(nextVertical);
    setPage(1);
  };

  const toggleGroup = (key) => {
    setExpanded((open) => {
      const next = new Set(open);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleStacking = (nextStack) => {
    setStack(nextStack);
    writeStackPreference(nextStack);
    setExpanded(new Set());
  };

  const openNotification = (notification) => {
    if (!notification.read_at) markRead.mutate(notification.id);
    const link = notificationLink(notification);
    if (link) navigate(link);
  };

  return (
    <Layout>
      <div className="w-full h-full">
        <div
          className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
            collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
          }`}>
          <Header data={{ name: 'Notifications' }} backArrow={true} />
        </div>

        <GridWrapper width={100}>
          <div className="mt-[6rem] mx-auto max-w-4xl pb-10">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => select(null)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    vertical === null
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}>
                  All{unreadTotal > 0 ? ` (${unreadTotal})` : ''}
                </button>

                {verticalsInPlay.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => select(name)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      vertical === name
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}>
                    {VERTICAL_LABELS[name] || name}
                    {byVertical[name] ? ` (${byVertical[name]})` : ''}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={unreadOnly}
                    onChange={(e) => {
                      setUnreadOnly(e.target.checked);
                      setPage(1);
                    }}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Unread only
                </label>
                <label
                  title="Rolls up same-day notifications of the same kind into one row"
                  className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={stack}
                    onChange={(e) => toggleStacking(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Group similar
                </label>
                {unreadTotal > 0 && (
                  <button
                    type="button"
                    onClick={() => markAll.mutate()}
                    disabled={markAll.isPending}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50">
                    Mark {vertical ? VERTICAL_LABELS[vertical] || vertical : 'all'} as read
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/builder/notifications/preferences')}
                  className="text-sm font-medium text-gray-600 hover:text-gray-800">
                  Settings
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              {isLoading && (
                <p className="px-6 py-12 text-center text-sm text-gray-500">Loading…</p>
              )}

              {isError && !isLoading && (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm font-medium text-gray-800">
                    Notifications could not be loaded
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Refresh the page to try again.
                  </p>
                </div>
              )}

              {!isLoading && !isError && notifications.length === 0 && (
                <div className="px-6 py-14 text-center">
                  <p className="text-sm font-medium text-gray-800">
                    {unreadOnly ? 'Nothing unread' : 'No notifications yet'}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    New leads, payments and task updates will show up here.
                  </p>
                </div>
              )}

              {days.map((day) => (
                <div key={day.key}>
                  {/*
                    Label only, no tally: this screen is paginated, so a day's
                    rows can continue on the next page and any count here would
                    describe the page rather than the day. The unread numbers
                    that are true are the server's, on the filter chips above.
                  */}
                  <div className="border-b border-gray-100 bg-gray-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {day.label}
                  </div>

                  {day.groups.map((group) => {
                    if (!group.isStack) {
                      return (
                        <NotificationRow
                          key={group.key}
                          notification={group.latest}
                          onOpen={openNotification}
                          onMarkRead={(id) => markRead.mutate(id)}
                        />
                      );
                    }

                    const isOpenGroup = expanded.has(group.key);
                    return (
                      <div key={group.key} className="border-b border-gray-100 last:border-b-0">
                        <div
                          className={`flex gap-3 px-5 py-4 ${
                            group.unreadCount > 0 ? 'bg-blue-50/40' : ''
                          }`}>
                          <span
                            className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                              group.unreadCount > 0
                                ? 'bg-blue-500'
                                : priorityDotClass(group.latest.priority)
                            }`}
                            aria-hidden="true"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                              <span className="flex min-w-0 items-baseline gap-2">
                                <span
                                  className={`text-sm ${
                                    group.unreadCount > 0
                                      ? 'font-semibold text-gray-900'
                                      : 'font-medium text-gray-800'
                                  }`}>
                                  {group.latest.title}
                                </span>
                                <span className="shrink-0 rounded-full bg-gray-200 px-2 text-[11px] font-semibold leading-5 text-gray-700">
                                  {group.count}
                                </span>
                              </span>
                              <span className="shrink-0 text-xs text-gray-400">
                                {relativeTime(group.latest.created_at)}
                              </span>
                            </div>
                            <p className="mt-1 text-sm leading-snug text-gray-600">
                              {group.latest.body}
                            </p>
                            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                              <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
                                {VERTICAL_LABELS[group.vertical] || group.vertical}
                              </span>
                              <span className="text-[11px] text-gray-400">
                                {stackSummary(group)}
                              </span>
                              <button
                                type="button"
                                aria-expanded={isOpenGroup}
                                onClick={() => toggleGroup(group.key)}
                                className="text-xs font-medium text-blue-600 hover:text-blue-700">
                                {isOpenGroup ? 'Hide' : `Show all ${group.count}`}
                              </button>
                            </div>
                          </div>

                          {group.unreadCount > 0 && (
                            <button
                              type="button"
                              onClick={() => markGroupRead.mutate(group.unreadIds)}
                              disabled={markGroupRead.isPending}
                              className="shrink-0 self-start text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50">
                              Mark {group.unreadCount} read
                            </button>
                          )}
                        </div>

                        {isOpenGroup && (
                          <div className="border-t border-gray-100 bg-gray-50/60 pl-5">
                            {group.items.map((notification) => (
                              <NotificationRow
                                key={notification.id}
                                notification={notification}
                                onOpen={openNotification}
                                onMarkRead={(id) => markRead.mutate(id)}
                                compact
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-40">
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-40">
                  Next
                </button>
              </div>
            )}
          </div>
        </GridWrapper>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
